import { type MouseEvent as ReactMouseEvent, type RefObject, useCallback, useEffect, useRef } from 'react';

import { ONE_SECOND, PERCENT_SCALE } from '@air/utilities';

import {
  getImageWheelGesture,
  getNormalizedImageWheelZoomDelta,
} from '~/components/AssetModalV2/Visualizer/ImageVisualizer/getImageWheelGesture';
import { MAX_ZOOM, MIN_ZOOM } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/shared/constants';
import { type ZoomToPointParams } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/shared/types';

const DEFAULT_TRANSITION = 0.25;
const DECELERATION_DURATION = 750;
const IMAGE_CENTER_FRACTION = 0.5;
const SCROLL_VELOCITY = 0.2;
const TOUCH_VELOCITY = 0.9;
const DEFAULT_TRANSITION_MS = DEFAULT_TRANSITION * ONE_SECOND;

// Determines how many pixels the image should be moved.
const getLimitedShift = ({
  shift,
  minLimit,
  maxLimit,
  minElement,
  maxElement,
}: {
  shift: number;
  minLimit: number;
  maxLimit: number;
  minElement: number;
  maxElement: number;
}) => {
  if (shift > 0) {
    if (minElement > minLimit) {
      // Forbid move if we are moving to left or top while we are already out minimum boudaries
      return 0;
    } else if (minElement + shift > minLimit) {
      // Lower the shift if we are going out boundaries
      return minLimit - minElement;
    }
  } else if (shift < 0) {
    if (maxElement < maxLimit) {
      // Forbid move if we are moving to right or bottom while we are already out maximum boudaries
      return 0;
    } else if (maxElement + shift < maxLimit) {
      // Lower the shift if we are going out boundaries
      return maxLimit - maxElement;
    }
  }

  return shift;
};

export type UseImageZoomProps = {
  imageRef: RefObject<HTMLImageElement>;
  imgContainerRef: RefObject<HTMLDivElement>;
  onViewChange?: (state: ImageZoomViewState) => void;
  onTrackpadPan?: (delta: { x: number; y: number }) => void;
  updateZoom: (value: number, options?: { syncTransitionDuration?: number }) => void;
  zoom: number;
  zoomOnSingleClickAtFit?: boolean;
};

export type ImageZoomViewState = {
  posX: number;
  posY: number;
  zoom: number;
};

export type ImageCenterFraction = {
  x: number;
  y: number;
};

type ImagePanPosition = {
  posX: number;
  posY: number;
};

export const useImageZoom = ({
  imageRef,
  imgContainerRef,
  onTrackpadPan,
  onViewChange,
  updateZoom,
  zoom,
  zoomOnSingleClickAtFit = false,
}: UseImageZoomProps) => {
  const lastRequestAnimationId = useRef<number | null>(null);
  const lastCursor = useRef<{ posX: number; posY: number } | null>(null);
  const lastShift = useRef<{ x: number; y: number } | null>(null);
  const posXRef = useRef<number>(0);
  const posYRef = useRef<number>(0);
  const zoomRef = useRef<number>(zoom); // Initialize with the current zoom value instead of MIN_ZOOM
  const transitionDuration = useRef<number>(DEFAULT_TRANSITION);
  const isMoving = useRef<boolean>(false);
  const isMouseWheelZoom = useRef<boolean>(false);
  // Arm after first internal click to avoid carry-over dblclick from opener
  const isArmedRef = useRef<boolean>(zoomOnSingleClickAtFit);
  const isClickZoomTransitioningRef = useRef<boolean>(false);
  const clickZoomTransitionTimeoutRef = useRef<number | null>(null);
  // Flag to indicate a click-initiated zoom so we can sync context without re-positioning
  const isClickZoom = useRef<boolean>(false);
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;
  const onTrackpadPanRef = useRef(onTrackpadPan);
  onTrackpadPanRef.current = onTrackpadPan;

  const cancelPendingAnimation = useCallback(() => {
    if (lastRequestAnimationId.current === null) {
      return;
    }

    cancelAnimationFrame(lastRequestAnimationId.current);
    lastRequestAnimationId.current = null;
  }, []);

  const cancelClickZoomTransition = useCallback(() => {
    if (clickZoomTransitionTimeoutRef.current !== null) {
      window.clearTimeout(clickZoomTransitionTimeoutRef.current);
      clickZoomTransitionTimeoutRef.current = null;
    }

    isClickZoomTransitioningRef.current = false;
  }, []);

  const startClickZoomTransition = useCallback(() => {
    cancelClickZoomTransition();
    isClickZoomTransitioningRef.current = true;
    clickZoomTransitionTimeoutRef.current = window.setTimeout(() => {
      clickZoomTransitionTimeoutRef.current = null;
      isClickZoomTransitioningRef.current = false;
    }, DEFAULT_TRANSITION_MS);
  }, [cancelClickZoomTransition]);

  // Repositions whatever imgRef is attached to.
  const repositionElement = useCallback(
    ({
      newPosY,
      newPosX,
      newTransitionDuration,
      newZoom,
    }: {
      newPosY: number;
      newPosX: number;
      newTransitionDuration?: number;
      newZoom?: number;
    }) => {
      if (newZoom && newZoom !== zoomRef.current) {
        zoomRef.current = newZoom;
      }

      if (newTransitionDuration !== undefined && newTransitionDuration !== transitionDuration.current) {
        transitionDuration.current = newTransitionDuration;
        if (imageRef.current && imgContainerRef.current) {
          imageRef.current.style.transition = `transform ease-out ${newTransitionDuration}s`;
          imgContainerRef.current.style.transition = `transform ease-out ${newTransitionDuration}s`;
        }
      }

      posXRef.current = newPosX;
      posYRef.current = newPosY;
      if (imgContainerRef.current) {
        imgContainerRef.current.style.transform = `translate3d(${newPosX}px, ${newPosY}px, 0)`;
      }
      if (imageRef.current) {
        // Use transform3d for hardware acceleration and consistency
        imageRef.current.style.transform = `translate3d(0, 0, 0) scale(${newZoom || zoomRef.current})`;
      }

      onViewChangeRef.current?.({ posX: newPosX, posY: newPosY, zoom: zoomRef.current });
    },
    [imageRef, imgContainerRef],
  );

  // Function that returns position coordinates if zooming in / out of a specific mouse point.
  const getNewPosition = useCallback(
    (x: number, y: number, newZoom: number) => {
      // When returning to 100%, always center
      if (newZoom === 1) {
        return [0, 0];
      }

      const prevPosY = posYRef.current;
      const prevPosX = posXRef.current;
      const prevZoom = zoomRef.current;

      // If we don't have the image ref, can't calculate properly
      if (!imageRef.current) {
        return [prevPosX, prevPosY];
      }

      // Get the actual image element bounds (not the container)
      const rect = imageRef.current.getBoundingClientRect();
      const imageCenterX = rect.left + rect.width / 2;
      const imageCenterY = rect.top + rect.height / 2;

      // Calculate mouse position relative to image center
      const deltaX = x - imageCenterX;
      const deltaY = y - imageCenterY;

      // Calculate how much the zoom is changing
      const zoomRatio = newZoom / prevZoom;

      // To keep the point under the cursor stationary:
      // We need to translate the image so that the point under the cursor doesn't move
      // The translation needed is the delta scaled by (1 - zoomRatio)
      const translateX = deltaX * (1 - zoomRatio);
      const translateY = deltaY * (1 - zoomRatio);

      // Add the translation to the current position
      return [prevPosX + translateX, prevPosY + translateY];
    },
    [imageRef],
  );

  // Max zooms into a specific mouse position
  const zoomInOnPosition = useCallback(
    (x: number, y: number, zoom: number) => {
      const sanitizedZoom = Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
      const [posX, posY] = getNewPosition(x, y, sanitizedZoom);

      repositionElement({
        newZoom: sanitizedZoom,
        newPosX: posX,
        newPosY: posY,
        newTransitionDuration: DEFAULT_TRANSITION,
      });
    },
    [getNewPosition, repositionElement],
  );

  // Moves the zoomed in image within its boundaries.
  const move = useCallback(
    (shiftX: number, shiftY: number) => {
      const image = imageRef.current;
      const parent = imgContainerRef.current?.parentElement;

      if (!image || !parent) {
        return { x: 0, y: 0 };
      }
      const previousPosX = posXRef.current;
      const previousPosY = posYRef.current;
      let posX = previousPosX;
      let posY = previousPosY;

      // Get container and container's parent coordinates
      const rect = image.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();

      const [isLarger, isOutLeftBoundary, isOutRightBoundary] = [
        // Check if the element is larger than its container
        rect.width > parentRect.right - parentRect.left,
        // Check if the element is out its container left boundary
        shiftX > 0 && rect.left - parentRect.left < 0,
        // Check if the element is out its container right boundary
        shiftX < 0 && rect.right - parentRect.right > 0,
      ];

      const canMoveOnX = isLarger || isOutLeftBoundary || isOutRightBoundary;

      if (canMoveOnX) {
        posX += getLimitedShift({
          shift: shiftX,
          minLimit: parentRect.left,
          maxLimit: parentRect.right,
          minElement: rect.left,
          maxElement: rect.right,
        });
      }

      const [isHigher, isOutTopBoundary, isOutBottomBoundary] = [
        // Check if the element is higher than its container
        rect.height > parentRect.bottom - parentRect.top,
        // Check if the element is out its container top boundary
        shiftY > 0 && rect.top - parentRect.top < 0,
        // Check if the element is out its container bottom boundary
        shiftY < 0 && rect.bottom - parentRect.bottom > 0,
      ];

      const canMoveOnY = isHigher || isOutTopBoundary || isOutBottomBoundary;

      if (canMoveOnY) {
        posY += getLimitedShift({
          shift: shiftY,
          minLimit: parentRect.top,
          maxLimit: parentRect.bottom,
          minElement: rect.top,
          maxElement: rect.bottom,
        });
      }

      repositionElement({
        newPosX: posX,
        newPosY: posY,
        newTransitionDuration: 0,
      });

      return { x: posX - previousPosX, y: posY - previousPosY };
    },
    [imageRef, imgContainerRef, repositionElement],
  );

  //Slows down the move, giving it a feeling of momentum.
  const startDeceleration = useCallback(
    (lastShiftOnX: number, lastShiftOnY: number) => {
      let startTimestamp: number | null = null;

      const moveFrame = (timestamp: number) => {
        if (startTimestamp === null) {
          startTimestamp = timestamp;
        }
        const progress = timestamp - startTimestamp;

        // Calculates the ratio to apply on the move (used to create a non-linear deceleration)
        const ratio = (DECELERATION_DURATION - progress) / DECELERATION_DURATION;

        const [shiftX, shiftY] = [lastShiftOnX * ratio, lastShiftOnY * ratio];

        // Continue animation only if time has not expired and if there is still some movement (more than 1 pixel on one axis)
        if (progress < DECELERATION_DURATION && Math.max(Math.abs(shiftX), Math.abs(shiftY)) > 1) {
          move(shiftX, shiftY);
          lastRequestAnimationId.current = requestAnimationFrame(moveFrame);
        } else {
          lastRequestAnimationId.current = null;
        }
      };

      lastRequestAnimationId.current = requestAnimationFrame(moveFrame);
    },
    [move],
  );

  // Zooms in / out of the current mouse position.
  const handleMouseWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();

      // Firefox can change the reported units after a delta is read, so capture the mode first.
      const deltaMode = event.deltaMode;
      const deltaX = event.deltaX;
      const deltaY = event.deltaY;
      const gesture = getImageWheelGesture({
        ctrlKey: event.ctrlKey,
        deltaMode,
        deltaX,
        deltaY,
        zoom: zoomRef.current,
      });

      if (gesture === 'none') {
        return;
      }

      if (gesture === 'pan') {
        cancelPendingAnimation();
        const shiftX = -deltaX;
        const shiftY = -deltaY;
        const appliedShift = move(shiftX, shiftY);
        onTrackpadPanRef.current?.(appliedShift);
        return;
      }

      let newZoom = zoomRef.current;
      let posX = posXRef.current;
      let posY = posYRef.current;

      const normalizedDeltaY = getNormalizedImageWheelZoomDelta({ deltaMode, deltaY });
      const isTrackpadPinch = event.ctrlKey && deltaMode === 0;
      const velocity = (-normalizedDeltaY * (isTrackpadPinch ? TOUCH_VELOCITY : SCROLL_VELOCITY)) / PERCENT_SCALE;
      newZoom = Math.max(Math.min(newZoom + velocity, MAX_ZOOM), MIN_ZOOM);
      // Round to 2 decimals for stability
      // Normalize zoom to slider precision (0.1 steps)
      newZoom = Math.round(newZoom * 10) / 10;

      if (newZoom !== zoomRef.current) {
        if (newZoom !== 1) {
          // Use shared math based on image center, with client coordinates
          const [nx, ny] = getNewPosition(event.clientX, event.clientY, newZoom);
          posX = nx;
          posY = ny;
        } else {
          // Reset to center when returning to 100%
          posX = 0;
          posY = 0;
        }

        // Mark that this zoom came from mouse wheel
        isMouseWheelZoom.current = true;

        // Apply the transform immediately
        // Important: disable transition during continuous wheel zoom to avoid
        // measuring mid-transition rects that cause drift when events are rapid.
        repositionElement({
          newZoom: newZoom,
          newPosX: posX,
          newPosY: posY,
          newTransitionDuration: 0,
        });

        // Sync UI state to exact zoom value (avoid stepping)
        updateZoom(newZoom, { syncTransitionDuration: 0 });
      }
    },
    [cancelPendingAnimation, getNewPosition, move, repositionElement, updateZoom],
  );

  // x and y are client (viewport) coordinates to align with getBoundingClientRect()
  const zoomToPoint = useCallback(
    ({ x, y, zoom, transitionDuration = 0.05 }: ZoomToPointParams) => {
      const [posX, posY] = getNewPosition(x, y, zoom);
      repositionElement({
        newZoom: zoom,
        newPosX: posX,
        newPosY: posY,
        newTransitionDuration: transitionDuration,
      });
      updateZoom(zoom, { syncTransitionDuration: transitionDuration });
    },
    [getNewPosition, repositionElement, updateZoom],
  );

  // Resets to default zoom. Use `instant` when switching clips to avoid a visible zoom settle.
  const reset = useCallback(
    ({ instant = false }: { instant?: boolean } = {}) => {
      cancelPendingAnimation();
      cancelClickZoomTransition();
      isMoving.current = false;
      lastCursor.current = null;
      lastShift.current = null;

      repositionElement({
        newZoom: MIN_ZOOM,
        newPosX: 0,
        newPosY: 0,
        newTransitionDuration: instant ? 0 : DEFAULT_TRANSITION,
      });
      updateZoom(MIN_ZOOM, { syncTransitionDuration: instant ? 0 : DEFAULT_TRANSITION });
    },
    [cancelClickZoomTransition, cancelPendingAnimation, repositionElement, updateZoom],
  );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      // Ignore the first in-modal click to prevent carry-over dblclick from opener; honor dblclicks thereafter.
      // First internal click arms the visualizer; ignore it.
      if (!isArmedRef.current) {
        isArmedRef.current = true;
        return;
      }

      // biome-ignore lint/suspicious/noUnnecessaryConditions: The timeout callback mutates this ref between click events.
      if (isClickZoomTransitioningRef.current) {
        return;
      }

      const zoomFactor = 5;

      const zoomFromClick = () => {
        event.preventDefault();

        if (zoomRef.current < MAX_ZOOM) {
          const targetZoom = Math.min(Math.max(zoomRef.current * zoomFactor, MIN_ZOOM), MAX_ZOOM);
          // Keep click zoom aligned with the 0.1 precision exposed by the zoom slider.
          const roundedTargetZoom = Math.round(targetZoom * 10) / 10;
          zoomInOnPosition(event.clientX, event.clientY, roundedTargetZoom);
          isClickZoom.current = true;
          updateZoom(roundedTargetZoom, { syncTransitionDuration: DEFAULT_TRANSITION });
          startClickZoomTransition();
          return;
        }

        reset();
        startClickZoomTransition();
      };

      if (zoomOnSingleClickAtFit && zoomRef.current === MIN_ZOOM) {
        zoomFromClick();
        return;
      }

      // Treat even-numbered clicks in a rapid sequence (2, 4, 6, ...) as double-click boundaries.
      if (event.detail % 2 !== 0) {
        return;
      }

      zoomFromClick();
    },
    [reset, startClickZoomTransition, updateZoom, zoomInOnPosition, zoomOnSingleClickAtFit],
  );

  const handleMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (zoomRef.current === MIN_ZOOM || !isMoving.current) {
        return;
      }
      event.preventDefault();

      // biome-ignore lint/suspicious/noUnnecessaryConditions: Pointer refs are written by separate DOM event callbacks.
      if (!lastCursor.current) {
        return;
      }

      const [posX, posY] = [event.clientX, event.clientY];
      const shiftX = posX - lastCursor.current.posX;
      const shiftY = posY - lastCursor.current.posY;

      move(shiftX, shiftY);
      lastCursor.current = { posX, posY };
      lastShift.current = { x: shiftX, y: shiftY };
    },
    [move],
  );

  const handleMouseStart = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (zoomRef.current === MIN_ZOOM) {
        return;
      }

      event.preventDefault();

      isMoving.current = true;

      cancelPendingAnimation();

      lastCursor.current = { posX: event.clientX, posY: event.clientY };
    },
    [cancelPendingAnimation],
  );

  const handleMouseStop = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const lastShiftBeforeStop = lastShift.current;

      isMoving.current = false;
      lastCursor.current = null;
      lastShift.current = null;

      if (zoomRef.current === MIN_ZOOM) {
        return;
      }

      event.preventDefault();

      // biome-ignore lint/suspicious/noUnnecessaryConditions: Pointer refs are written by separate DOM event callbacks.
      if (lastShiftBeforeStop) {
        // Use the last shift to make a decelerating movement effect
        startDeceleration(lastShiftBeforeStop.x, lastShiftBeforeStop.y);
      }
    },
    [startDeceleration],
  );

  const panToImageFraction = useCallback(
    ({
      x,
      y,
      transitionDuration: panTransitionDuration = 0,
    }: {
      x: number;
      y: number;
      transitionDuration?: number;
    }) => {
      const image = imageRef.current;
      const container = imgContainerRef.current?.parentElement;

      if (
        !image ||
        !container ||
        zoomRef.current <= MIN_ZOOM ||
        !Number.isFinite(x) ||
        !Number.isFinite(y) ||
        !Number.isFinite(panTransitionDuration)
      ) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();

      if (containerRect.width <= 0 || containerRect.height <= 0 || imageRect.width <= 0 || imageRect.height <= 0) {
        return;
      }

      const imageFractionX = Math.min(1, Math.max(0, x));
      const imageFractionY = Math.min(1, Math.max(0, y));
      const maxX = Math.max(0, (imageRect.width - containerRect.width) / 2);
      const maxY = Math.max(0, (imageRect.height - containerRect.height) / 2);
      const horizontalFractionFromCenter = IMAGE_CENTER_FRACTION - imageFractionX;
      const verticalFractionFromCenter = IMAGE_CENTER_FRACTION - imageFractionY;
      const nextPosX = Math.min(maxX, Math.max(-maxX, imageRect.width * horizontalFractionFromCenter));
      const nextPosY = Math.min(maxY, Math.max(-maxY, imageRect.height * verticalFractionFromCenter));

      repositionElement({
        newPosX: nextPosX,
        newPosY: nextPosY,
        newTransitionDuration: Math.max(0, panTransitionDuration),
      });
    },
    [imageRef, imgContainerRef, repositionElement],
  );

  const panByDelta = useCallback(
    ({ x, y }: { x: number; y: number }) => {
      move(x, y);
    },
    [move],
  );

  const getImageCenterFraction = useCallback((): ImageCenterFraction | undefined => {
    const image = imageRef.current;

    if (!image) {
      return undefined;
    }

    const scaledImageWidth = image.offsetWidth * zoomRef.current;
    const scaledImageHeight = image.offsetHeight * zoomRef.current;

    if (
      scaledImageWidth <= 0 ||
      scaledImageHeight <= 0 ||
      !Number.isFinite(scaledImageWidth) ||
      !Number.isFinite(scaledImageHeight) ||
      !Number.isFinite(posXRef.current) ||
      !Number.isFinite(posYRef.current)
    ) {
      return undefined;
    }

    return {
      x: Math.min(1, Math.max(0, IMAGE_CENTER_FRACTION - posXRef.current / scaledImageWidth)),
      y: Math.min(1, Math.max(0, IMAGE_CENTER_FRACTION - posYRef.current / scaledImageHeight)),
    };
  }, [imageRef]);

  const getPanPositionForImageFraction = useCallback(
    ({ x, y, zoom: targetZoom }: ImageCenterFraction & { zoom: number }): ImagePanPosition | undefined => {
      const image = imageRef.current;
      const container = imgContainerRef.current?.parentElement;

      if (!image || !container || !Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(targetZoom)) {
        return undefined;
      }

      const scaledImageWidth = image.offsetWidth * targetZoom;
      const scaledImageHeight = image.offsetHeight * targetZoom;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      if (
        scaledImageWidth <= 0 ||
        scaledImageHeight <= 0 ||
        containerWidth <= 0 ||
        containerHeight <= 0 ||
        !Number.isFinite(scaledImageWidth) ||
        !Number.isFinite(scaledImageHeight)
      ) {
        return undefined;
      }

      const imageFractionX = Math.min(1, Math.max(0, x));
      const imageFractionY = Math.min(1, Math.max(0, y));
      const maxX = Math.max(0, (scaledImageWidth - containerWidth) / 2);
      const maxY = Math.max(0, (scaledImageHeight - containerHeight) / 2);

      return {
        posX: Math.min(maxX, Math.max(-maxX, scaledImageWidth * (IMAGE_CENTER_FRACTION - imageFractionX))),
        posY: Math.min(maxY, Math.max(-maxY, scaledImageHeight * (IMAGE_CENTER_FRACTION - imageFractionY))),
      };
    },
    [imageRef, imgContainerRef],
  );

  const syncZoomFromPeer = useCallback(
    ({
      centerFraction,
      transitionDuration: nextTransitionDuration,
      zoom: nextZoom,
    }: {
      centerFraction: ImageCenterFraction | undefined;
      transitionDuration: number;
      zoom: number;
    }) => {
      if (nextZoom === MIN_ZOOM) {
        repositionElement({
          newPosX: 0,
          newPosY: 0,
          newTransitionDuration: nextTransitionDuration,
          newZoom: nextZoom,
        });
        return;
      }

      const centerPosition = centerFraction
        ? getPanPositionForImageFraction({ ...centerFraction, zoom: nextZoom })
        : undefined;

      if (centerPosition) {
        repositionElement({
          newPosX: centerPosition.posX,
          newPosY: centerPosition.posY,
          newTransitionDuration: nextTransitionDuration,
          newZoom: nextZoom,
        });
        return;
      }

      if (nextZoom !== zoomRef.current) {
        const previousZoom = zoomRef.current;
        let nextPosX = 0;
        let nextPosY = 0;

        if (previousZoom > MIN_ZOOM) {
          nextPosX = (posXRef.current * (nextZoom - MIN_ZOOM)) / (previousZoom - MIN_ZOOM);
          nextPosY = (posYRef.current * (nextZoom - MIN_ZOOM)) / (previousZoom - MIN_ZOOM);
        }

        repositionElement({
          newPosX: nextPosX,
          newPosY: nextPosY,
          newTransitionDuration: nextTransitionDuration,
          newZoom: nextZoom,
        });
      }
    },
    [getPanPositionForImageFraction, repositionElement],
  );

  // Update zoomRef and reposition when zoom changes
  useEffect(() => {
    // Always clear the mouse wheel zoom flag at the start of this effect
    // This ensures that keyboard shortcuts work even after mouse wheel zoom
    const wasMouseWheelZoom = isMouseWheelZoom.current;
    isMouseWheelZoom.current = false;

    if (zoom !== zoomRef.current) {
      // If this zoom came from mouse wheel, skip processing
      // (we already handled it synchronously in handleMouseWheel)
      // biome-ignore lint/suspicious/noUnnecessaryConditions: The wheel callback mutates this ref before the state effect runs.
      if (wasMouseWheelZoom) {
        zoomRef.current = zoom;
        return;
      }

      // If this zoom came from a click-initiated zoom (double-click),
      // we already applied the exact transform synchronously.
      // biome-ignore lint/suspicious/noUnnecessaryConditions: The click callback mutates this ref before the state effect runs.
      if (isClickZoom.current) {
        isClickZoom.current = false;
        zoomRef.current = zoom;
        return;
      }

      const prevZoom = zoomRef.current;
      zoomRef.current = zoom;

      let posX = 0;
      let posY = 0;

      // This is for button zoom or other non-mouse-wheel zoom
      if (prevZoom > 1 && zoom > 1) {
        // Moving between zoomed states without mouse position
        posX = (posXRef.current * (zoom - 1)) / (prevZoom - 1);
        posY = (posYRef.current * (zoom - 1)) / (prevZoom - 1);
      }
      // Otherwise use 0, 0 (centered)

      repositionElement({
        newZoom: zoom,
        newPosX: posX,
        newPosY: posY,
        newTransitionDuration: DEFAULT_TRANSITION,
      });
    }
  }, [zoom, repositionElement]);

  // Ensure zoomRef is synchronized on mount and when zoom changes
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  // Ensure click-initiated zoom suppression flag is cleared on any zoom change,
  // even when zoomRef already matches and the main effect short-circuits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: if `zoom` changes, we want to clear `isClickZoom`
  useEffect(() => {
    isClickZoom.current = false;
  }, [zoom]);

  // Initialize event handlers
  useEffect(() => {
    const imageContainer = imgContainerRef.current;

    if (!imageContainer || !imageRef.current) {
      return;
    }

    imageContainer.addEventListener('click', handleClick);
    imageContainer.addEventListener('wheel', handleMouseWheel, { passive: false });

    return () => {
      imageContainer.removeEventListener('click', handleClick);
      imageContainer.removeEventListener('wheel', handleMouseWheel);
      cancelPendingAnimation();
      cancelClickZoomTransition();
      isMoving.current = false;
      lastCursor.current = null;
      lastShift.current = null;
    };
  }, [cancelClickZoomTransition, cancelPendingAnimation, handleClick, handleMouseWheel, imageRef, imgContainerRef]);

  return {
    getImageCenterFraction,
    handleMouseMove,
    handleMouseStart,
    handleMouseStop,
    panByDelta,
    panToImageFraction,
    reset,
    syncZoomFromPeer,
    zoomToPoint,
  };
};
